// Industry vertical (Axis A) of the school taxonomy.
//
// Two levels:
//   - BusinessTypes      — the specific vertical (37 values). Stored on the
//                          schoolClassifications row.
//   - BusinessTypeGroups — 8 coarse groups, DERIVED from the specific type via
//                          BusinessTypeToGroup (single source of truth). Never an
//                          independent guess — persisted only as a computed cache.
//
// The Prisma `enum BusinessType` (schools.prisma) and the Mongoose
// `school.model.js` validation read from `BusinessTypes`, so this list must stay
// in sync with the Prisma enum by hand.

export enum BusinessTypes {
  // fitness_wellness
  yoga = "yoga",
  pilates = "pilates",
  martial_arts = "martial_arts",
  fitness = "fitness",
  barre = "barre",
  massage_therapy = "massage_therapy",
  acupuncture = "acupuncture",
  wellness = "wellness",
  // performing_arts
  dance = "dance",
  music = "music",
  art_craft = "art_craft",
  cooking = "cooking",
  pottery_ceramics = "pottery_ceramics",
  // beauty_personal_care
  hair_salon = "hair_salon",
  barbershop = "barbershop",
  nails_lash = "nails_lash",
  tattoo_piercing = "tattoo_piercing",
  medspa = "medspa",
  // sports_recreation
  swimming = "swimming",
  sports = "sports",
  racquet_sports = "racquet_sports",
  golf_sim = "golf_sim",
  gymnastics_cheer = "gymnastics_cheer",
  // education_enrichment
  tutoring = "tutoring",
  language = "language",
  kids_activities = "kids_activities",
  stem_coding = "stem_coding",
  test_prep = "test_prep",
  driving_school = "driving_school",
  // entertainment_leisure
  escape_room = "escape_room",
  axe_throwing = "axe_throwing",
  kids_party_venue = "kids_party_venue",
  photography_studio = "photography_studio",
  // pet_services
  dog_training = "dog_training",
  pet_grooming = "pet_grooming",
  pet_daycare = "pet_daycare",
  // other
  other = "other"
}

export const BusinessTypesLabels: Record<BusinessTypes, string> = {
  [BusinessTypes.yoga]: "Yoga",
  [BusinessTypes.pilates]: "Pilates",
  [BusinessTypes.martial_arts]: "Martial Arts",
  [BusinessTypes.fitness]: "Fitness / Gym",
  [BusinessTypes.barre]: "Barre",
  [BusinessTypes.massage_therapy]: "Massage Therapy",
  [BusinessTypes.acupuncture]: "Acupuncture",
  [BusinessTypes.wellness]: "Wellness / Spa",
  [BusinessTypes.dance]: "Dance",
  [BusinessTypes.music]: "Music",
  [BusinessTypes.art_craft]: "Art & Craft",
  [BusinessTypes.cooking]: "Cooking",
  [BusinessTypes.pottery_ceramics]: "Pottery & Ceramics",
  [BusinessTypes.hair_salon]: "Hair Salon",
  [BusinessTypes.barbershop]: "Barbershop",
  [BusinessTypes.nails_lash]: "Nails & Lash",
  [BusinessTypes.tattoo_piercing]: "Tattoo & Piercing",
  [BusinessTypes.medspa]: "Med Spa",
  [BusinessTypes.swimming]: "Swimming",
  [BusinessTypes.sports]: "Sports / Coaching",
  [BusinessTypes.racquet_sports]: "Racquet Sports",
  [BusinessTypes.golf_sim]: "Golf / Simulators",
  [BusinessTypes.gymnastics_cheer]: "Gymnastics & Cheer",
  [BusinessTypes.tutoring]: "Tutoring / Academic",
  [BusinessTypes.language]: "Language School",
  [BusinessTypes.kids_activities]: "Kids Activities",
  [BusinessTypes.stem_coding]: "STEM / Coding",
  [BusinessTypes.test_prep]: "Test Prep",
  [BusinessTypes.driving_school]: "Driving School",
  [BusinessTypes.escape_room]: "Escape Room",
  [BusinessTypes.axe_throwing]: "Axe Throwing",
  [BusinessTypes.kids_party_venue]: "Kids Party Venue",
  [BusinessTypes.photography_studio]: "Photography Studio",
  [BusinessTypes.dog_training]: "Dog Training",
  [BusinessTypes.pet_grooming]: "Pet Grooming",
  [BusinessTypes.pet_daycare]: "Pet Daycare",
  [BusinessTypes.other]: "Other"
};

/**
 * Industry groups (the coarse, queryable level). DERIVED from BusinessTypes via
 * BusinessTypeToGroup — never set independently.
 */
export enum BusinessTypeGroups {
  fitness_wellness = "fitness_wellness",
  performing_arts = "performing_arts",
  beauty_personal_care = "beauty_personal_care",
  sports_recreation = "sports_recreation",
  education_enrichment = "education_enrichment",
  entertainment_leisure = "entertainment_leisure",
  pet_services = "pet_services",
  other = "other"
}

export const BusinessTypeGroupLabels: Record<BusinessTypeGroups, string> = {
  [BusinessTypeGroups.fitness_wellness]: "Fitness, Health & Wellness",
  [BusinessTypeGroups.performing_arts]: "Performing Arts & Creative",
  [BusinessTypeGroups.beauty_personal_care]: "Beauty & Personal Care",
  [BusinessTypeGroups.sports_recreation]: "Sports & Recreation",
  [BusinessTypeGroups.education_enrichment]: "Education & Enrichment",
  [BusinessTypeGroups.entertainment_leisure]: "Entertainment & Leisure",
  [BusinessTypeGroups.pet_services]: "Pet Services",
  [BusinessTypeGroups.other]: "Other"
};

/** Ordered group keys — single source of truth for iteration order. */
export const BusinessTypeGroupsOrdered: BusinessTypeGroups[] = [
  BusinessTypeGroups.fitness_wellness,
  BusinessTypeGroups.performing_arts,
  BusinessTypeGroups.beauty_personal_care,
  BusinessTypeGroups.sports_recreation,
  BusinessTypeGroups.education_enrichment,
  BusinessTypeGroups.entertainment_leisure,
  BusinessTypeGroups.pet_services,
  BusinessTypeGroups.other
];

/**
 * The single source of truth mapping a specific vertical → its industry group.
 * Every writer that sets `businessType` must recompute `industryGroup` from this
 * map (use {@link getIndustryGroup}). If this map changes, re-derive the cached
 * `industryGroup` on every schoolClassifications row via a backfill.
 */
export const BusinessTypeToGroup: Record<BusinessTypes, BusinessTypeGroups> = {
  [BusinessTypes.yoga]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.pilates]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.martial_arts]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.fitness]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.barre]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.massage_therapy]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.acupuncture]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.wellness]: BusinessTypeGroups.fitness_wellness,
  [BusinessTypes.dance]: BusinessTypeGroups.performing_arts,
  [BusinessTypes.music]: BusinessTypeGroups.performing_arts,
  [BusinessTypes.art_craft]: BusinessTypeGroups.performing_arts,
  [BusinessTypes.cooking]: BusinessTypeGroups.performing_arts,
  [BusinessTypes.pottery_ceramics]: BusinessTypeGroups.performing_arts,
  [BusinessTypes.hair_salon]: BusinessTypeGroups.beauty_personal_care,
  [BusinessTypes.barbershop]: BusinessTypeGroups.beauty_personal_care,
  [BusinessTypes.nails_lash]: BusinessTypeGroups.beauty_personal_care,
  [BusinessTypes.tattoo_piercing]: BusinessTypeGroups.beauty_personal_care,
  [BusinessTypes.medspa]: BusinessTypeGroups.beauty_personal_care,
  [BusinessTypes.swimming]: BusinessTypeGroups.sports_recreation,
  [BusinessTypes.sports]: BusinessTypeGroups.sports_recreation,
  [BusinessTypes.racquet_sports]: BusinessTypeGroups.sports_recreation,
  [BusinessTypes.golf_sim]: BusinessTypeGroups.sports_recreation,
  [BusinessTypes.gymnastics_cheer]: BusinessTypeGroups.sports_recreation,
  [BusinessTypes.tutoring]: BusinessTypeGroups.education_enrichment,
  [BusinessTypes.language]: BusinessTypeGroups.education_enrichment,
  [BusinessTypes.kids_activities]: BusinessTypeGroups.education_enrichment,
  [BusinessTypes.stem_coding]: BusinessTypeGroups.education_enrichment,
  [BusinessTypes.test_prep]: BusinessTypeGroups.education_enrichment,
  [BusinessTypes.driving_school]: BusinessTypeGroups.education_enrichment,
  [BusinessTypes.escape_room]: BusinessTypeGroups.entertainment_leisure,
  [BusinessTypes.axe_throwing]: BusinessTypeGroups.entertainment_leisure,
  [BusinessTypes.kids_party_venue]: BusinessTypeGroups.entertainment_leisure,
  [BusinessTypes.photography_studio]: BusinessTypeGroups.entertainment_leisure,
  [BusinessTypes.dog_training]: BusinessTypeGroups.pet_services,
  [BusinessTypes.pet_grooming]: BusinessTypeGroups.pet_services,
  [BusinessTypes.pet_daycare]: BusinessTypeGroups.pet_services,
  [BusinessTypes.other]: BusinessTypeGroups.other
};

/**
 * Derive the cached industry group for a (possibly absent / unknown) business
 * type. Returns null when there is no business type or it isn't a known value,
 * so callers can persist `industryGroup` consistently in the same write.
 */
export function getIndustryGroup(
  businessType?: string | null
): BusinessTypeGroups | null {
  if (!businessType) return null;
  return BusinessTypeToGroup[businessType as BusinessTypes] ?? null;
}

/**
 * Ordered specific types, grouped by industry group (group order follows
 * BusinessTypeGroupsOrdered). Single source of truth for picklist order — the
 * /start combobox renders these split into shadcn Command groups.
 */
export const BusinessTypesOrdered: BusinessTypes[] = [
  // fitness_wellness
  BusinessTypes.yoga,
  BusinessTypes.pilates,
  BusinessTypes.martial_arts,
  BusinessTypes.fitness,
  BusinessTypes.barre,
  BusinessTypes.massage_therapy,
  BusinessTypes.acupuncture,
  BusinessTypes.wellness,
  // performing_arts
  BusinessTypes.dance,
  BusinessTypes.music,
  BusinessTypes.art_craft,
  BusinessTypes.cooking,
  BusinessTypes.pottery_ceramics,
  // beauty_personal_care
  BusinessTypes.hair_salon,
  BusinessTypes.barbershop,
  BusinessTypes.nails_lash,
  BusinessTypes.tattoo_piercing,
  BusinessTypes.medspa,
  // sports_recreation
  BusinessTypes.swimming,
  BusinessTypes.sports,
  BusinessTypes.racquet_sports,
  BusinessTypes.golf_sim,
  BusinessTypes.gymnastics_cheer,
  // education_enrichment
  BusinessTypes.tutoring,
  BusinessTypes.language,
  BusinessTypes.kids_activities,
  BusinessTypes.stem_coding,
  BusinessTypes.test_prep,
  BusinessTypes.driving_school,
  // entertainment_leisure
  BusinessTypes.escape_room,
  BusinessTypes.axe_throwing,
  BusinessTypes.kids_party_venue,
  BusinessTypes.photography_studio,
  // pet_services
  BusinessTypes.dog_training,
  BusinessTypes.pet_grooming,
  BusinessTypes.pet_daycare,
  // other
  BusinessTypes.other
];
